import {gql} from '@apollo/client';
import {StyleSheet, css} from 'aphrodite';
import React, {useMemo} from 'react';

import {ArchiveOptions} from 'archive/ArchiveConstants';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import {
  BenchlingQueryResult,
  QueryResultType,
  SuccessReturnType,
  useBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {TemplateCollectibleNameCell} from 'notebook/entry-templates/collections/TemplateCollectionTable';
import CreateRequestV2TemplateModal from 'processes/requests_v2/modals/CreateRequestV2TemplateModal';
import {updateRequestV2Definition} from 'processes/requests_v2/network/RequestV2DefinitionDAO';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import EditableItemTable, {EditableItemRowProps} from 'ui-library/EditableItemTable';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {Header1} from 'ui-library/Headers';
import runModal from 'ui-library/runModal';
import {readableTime} from 'util/DateUtil';
import sortedBy from 'util/sortedBy';

import {
  RequestV2DefinitionsForTaskSchemaQuery,
  RequestV2DefinitionsForTaskSchemaQueryVariables,
  RequestV2DefinitionsForTaskSchemaQuery_processTaskSchema_ProcessTaskSchemaNode as ProcessTaskSchemaGql,
  RequestV2DefinitionsForTaskSchemaQuery_processTaskSchema_ProcessTaskSchemaNode_requestV2Definitions_RequestV2DefinitionNode as RequestV2DefinitionGql,
} from './__generated/RequestV2DefinitionsForTaskSchemaQuery';

export const REQUEST_V2_DEFINITIONS_FOR_TASK_SCHEMA_QUERY = gql`
  query RequestV2DefinitionsForTaskSchemaQuery($taskSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTaskSchema(id: $taskSchemaId) {
      ... on ProcessTaskSchemaNode {
        id
        name
        requestV2Definitions {
          ... on RequestV2DefinitionNode {
            id
            name
            editUrl
            creator {
              id
              name
            }
            createdAt
            modifiedAt
            templateCollection {
              id
              name
              url
            }
            archiveData {
              archived
              purpose
            }
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ProcessTaskSchemaRequestV2DefinitionsListProps {
  taskSchemaId: string;
}

export const COLUMN_HEADERS: Array<{name: string; width: number}> = [
  {name: 'Name', width: 4},
  {name: 'Author', width: 2},
  {name: 'Created', width: 1},
  {name: 'Last modified', width: 2},
  {name: 'Template collection', width: 3},
];

function resultIsAccessibleTaskSchema(
  queryResult: BenchlingQueryResult<RequestV2DefinitionsForTaskSchemaQuery>
): queryResult is SuccessReturnType<
  RequestV2DefinitionsForTaskSchemaQuery & {processTaskSchema: ProcessTaskSchemaGql}
> {
  return queryResult.type === QueryResultType.SUCCESS && !isInaccessible(queryResult.data.processTaskSchema);
}

export default function ProcessTaskSchemaRequestV2DefinitionsList(
  props: ProcessTaskSchemaRequestV2DefinitionsListProps
): JSX.Element {
  const result = useBenchlingQuery<
    RequestV2DefinitionsForTaskSchemaQuery,
    RequestV2DefinitionsForTaskSchemaQueryVariables
  >(REQUEST_V2_DEFINITIONS_FOR_TASK_SCHEMA_QUERY, {
    variables: {taskSchemaId: props.taskSchemaId},
    fetchPolicy: 'cache-and-network',
  });

  const accessibleRequestDefinitions: Array<RequestV2DefinitionGql> = useMemo(() => {
    if (!resultIsAccessibleTaskSchema(result)) {
      return [];
    }
    return sortedBy(
      result.data.processTaskSchema.requestV2Definitions.filter((def) => !isInaccessible(def)),
      [(def) => def.name.toLowerCase()]
    );
  }, [result]);

  const numActiveRequestV2Definitions = accessibleRequestDefinitions.filter((def) => !isArchived(def)).length;
  const requestV2DefinitionCreationDisabled =
    numActiveRequestV2Definitions >= BenchlingConfig.REQUESTS_V2_MAX_REQUEST_TEMPLATES_PER_TASK_SCHEMA;

  const rows: Array<EditableItemRowProps> = accessibleRequestDefinitions.map((definition, index) => {
    const values = [
      <TemplateCollectibleNameCell
        templateCollectibleInfo={{
          ...definition,
          // We need to transform the domain graph archiveData into the archiveRecord shape expected by the cell
          archiveRecord: definition.archiveData?.archived
            ? {
                __typename: 'ArchiveRecordNode',
                purpose: definition.archiveData.purpose!,
              }
            : null,
        }}
      />,
      definition.creator.name,
      readableTime(definition.createdAt, {showTime: false}),
      readableTime(definition.modifiedAt, {showTime: false}),
      <Button
        onClick={(event) => {
          event.stopPropagation();
          BenchlingUtil.navigate(event, definition.templateCollection.url);
        }}
        buttonStyle={ButtonStyle.LINK}
        text={definition.templateCollection.name}
      />,
    ];
    return {
      id: definition.id,
      itemIndex: index,
      isArchived: isArchived(definition),
      values,
    };
  });

  return (
    <div className={css(styles.list)}>
      <EditableItemTable
        columnHeaders={COLUMN_HEADERS}
        emptyMessage="No request templates found for this schema"
        isLoading={result.type === QueryResultType.PENDING}
        rows={rows}
        getEditUrl={(index) => accessibleRequestDefinitions[index].editUrl}
        writable={true}
        onArchive={(itemIndex) => {
          const requestV2Definition = accessibleRequestDefinitions[itemIndex];
          ArchiveModal.open({
            items: [{id: requestV2Definition.id, name: requestV2Definition.name}],
            onArchive: async (itemIds: Array<string>, purpose: string, opts: ArchiveOptions) => {
              assert(
                itemIds.length === 1 && itemIds[0] === requestV2Definition.id,
                'Expected exactly one request template to be archived',
                {itemIds, requestV2DefinitionId: requestV2Definition.id}
              );
              await updateRequestV2Definition(
                {
                  id: requestV2Definition.id,
                  isArchived: true,
                  archiveReason: purpose,
                  actionOrigin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE,
                  actionMethod: ActionMethod.BUTTON_CLICK,
                },
                'Archiving request template...'
              );
              result.refetch();
            },
          });
        }}
        onUnarchive={async (itemIndex) => {
          const requestV2Definition = accessibleRequestDefinitions[itemIndex];
          await updateRequestV2Definition(
            {
              id: requestV2Definition.id,
              isArchived: false,
              actionOrigin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE,
              actionMethod: ActionMethod.BUTTON_CLICK,
            },
            'Unarchiving request template...'
          );
          result.refetch();
        }}
        leftSubheaderNode={
          resultIsAccessibleTaskSchema(result) && accessibleRequestDefinitions.length > 0 ? (
            <div className={css(styles.headerWrapper)}>
              <Header1>
                {accessibleRequestDefinitions.length}&nbsp;
                {BenchlingUtil.pluralize(accessibleRequestDefinitions.length, 'template')}
              </Header1>
            </div>
          ) : null
        }
        rightSubheaderNode={
          resultIsAccessibleTaskSchema(result) &&
          isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_ADMIN}) ? (
            <Tooltip
              text={
                requestV2DefinitionCreationDisabled
                  ? `This schema is already associated with ${numActiveRequestV2Definitions} request ${BenchlingUtil.pluralize(numActiveRequestV2Definitions, 'template')}.`
                  : undefined
              }
            >
              <Button
                buttonStyle={ButtonStyle.PRIMARY}
                size={ButtonSize.MEDIUM}
                text="Create"
                disabled={requestV2DefinitionCreationDisabled}
                onClick={async () => {
                  assert(
                    resultIsAccessibleTaskSchema(result),
                    'Cannot create request definition for inaccessible task schema'
                  );

                  await runModal(CreateRequestV2TemplateModal, {
                    initialTaskSchema: {
                      id: props.taskSchemaId,
                      name: result.data.processTaskSchema.name,
                    },
                    taskSchemaCannotChange: true,
                    actionOrigin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE,
                  });
                }}
                dataTestAttrs={{
                  component: 'ProcessTaskSchemaRequestV2DefinitionsList',
                  element: 'createButton',
                }}
              />
            </Tooltip>
          ) : null
        }
      />
    </div>
  );
}

const styles = StyleSheet.create({
  list: {
    ...FONT_PROXIMA_13,
  },
  headerWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
  },
});
